import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  fetchThemeCustomPlans,
  saveThemeCustomPlans,
  deleteThemeCustomPlans,
} from "../../../slices/admin/thunk";
import HeadersRole from "../../../Components/Headers/HeadersRole";
import adminIcon from "../../../assets/images/iconsRoles/adminIcon.svg";
import { DASHBOARD_USER_ROLES } from "../../../constants";
import { useCurrentUser } from "../../../slices/auth/hooks";

const ThemeCustomPlans = () => {
  const { nationalId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const firstPlanIdRef = useRef(null);

  const [plans, setPlans] = useState([]);
  const [originalPlans, setOriginalPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasCustomPlans, setHasCustomPlans] = useState(false);
  const [themeName, setThemeName] = useState("");

  const isDirty = JSON.stringify(plans) !== JSON.stringify(originalPlans);

  // Check admin access
  useEffect(() => {
    if (!currentUser || currentUser.role !== DASHBOARD_USER_ROLES.ADMIN) {
      Swal.fire({
        title: 'Access Denied',
        text: 'Admin access required',
        icon: 'error',
        confirmButtonColor: '#f06548'
      }).then(() => {
        navigate("/dashboard");
      });
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (nationalId) {
      loadPlans();
    }
  }, [nationalId]);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchThemeCustomPlans({ nationalId })).unwrap();

      // Convert cents to dollars for UI
      const plansInDollars = response.customPlans.map(plan => ({
        ...plan,
        amount: plan.amount / 100,
      }));

      setPlans(plansInDollars);
      setOriginalPlans(plansInDollars);
      setHasCustomPlans(response.hasCustomPlans);
      setThemeName(response.themeName || nationalId);
    } catch (error) {
      console.error("Error loading plans:", error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to load custom plans',
        icon: 'error',
        confirmButtonColor: '#f06548'
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePlans = (plansToValidate) => {
    const errors = [];

    if (plansToValidate.length === 0) {
      errors.push('At least one plan is required');
      return errors;
    }

    plansToValidate.forEach((plan, index) => {
      if (!plan.id?.trim()) {
        errors.push(`Plan ${index + 1}: ID is required`);
      }

      if (plan.id && !/^[a-z0-9-]+$/i.test(plan.id)) {
        errors.push(`Plan ${index + 1}: ID can only contain letters, numbers, and dashes`);
      }

      if (!plan.name?.trim()) {
        errors.push(`Plan ${index + 1}: Name is required`);
      }

      if (plan.amount < 0) {
        errors.push(`Plan ${index + 1}: Amount must be 0 or greater`);
      }

      if (!['monthly', 'yearly'].includes(plan.frequency)) {
        errors.push(`Plan ${index + 1}: Frequency must be monthly or yearly`);
      }
    });

    // Check for unique IDs
    const ids = plansToValidate.map(p => p.id).filter(id => id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      errors.push('Plan IDs must be unique');
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validatePlans(plans);
    if (errors.length > 0) {
      Swal.fire({
        title: 'Validation Error',
        text: errors[0],
        icon: 'warning',
        confirmButtonColor: '#f1b44c'
      });
      return;
    }

    setSaving(true);
    try {
      // Convert dollars to cents for API
      const plansInCents = plans.map(plan => ({
        ...plan,
        amount: Math.round(plan.amount * 100),
      }));

      await dispatch(saveThemeCustomPlans({
        nationalId,
        customPlans: plansInCents
      })).unwrap();

      setOriginalPlans(plans);
      setHasCustomPlans(true);

      Swal.fire({
        title: 'Success!',
        text: 'Custom plans saved successfully',
        icon: 'success',
        confirmButtonColor: '#0ab39c',
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error("Error saving plans:", error);
      const errorMessage = getErrorMessage(error.message);
      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#f06548'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Remove Custom Plans?',
      text: 'This theme will use the default plans. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Remove Plans',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    setDeleting(true);
    try {
      await dispatch(deleteThemeCustomPlans({ nationalId })).unwrap();

      setPlans([]);
      setOriginalPlans([]);
      setHasCustomPlans(false);

      Swal.fire({
        title: 'Removed!',
        text: 'Custom plans removed. Now using default plans.',
        icon: 'success',
        confirmButtonColor: '#0ab39c'
      });
    } catch (error) {
      console.error("Error deleting plans:", error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete custom plans',
        icon: 'error',
        confirmButtonColor: '#f06548'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = async () => {
    if (isDirty) {
      const result = await Swal.fire({
        title: 'Discard Changes?',
        text: 'You have unsaved changes. Are you sure you want to leave?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Discard',
        cancelButtonText: 'Stay',
      });

      if (!result.isConfirmed) return;
    }

    navigate(-1);
  };

  const addPlan = () => {
    const newPlan = {
      id: '',
      name: '',
      amount: 0,
      frequency: 'monthly',
      description: '',
      isFamilyPlan: false,
      isFreePlan: false,
    };

    setPlans([...plans, newPlan]);

    // Scroll to new plan and focus
    setTimeout(() => {
      if (firstPlanIdRef.current) {
        firstPlanIdRef.current.focus();
      }
    }, 100);
  };

  const removePlan = async (index) => {
    if (plans.length === 1) {
      const result = await Swal.fire({
        title: 'Remove last plan?',
        text: 'Are you sure you want to remove the last plan?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;
    }

    setPlans(plans.filter((_, i) => i !== index));
  };

  const updatePlan = (index, field, value) => {
    const newPlans = [...plans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setPlans(newPlans);
  };

  const getErrorMessage = (error) => {
    const messages = {
      'customPlans must be an array': 'Invalid data format. Please refresh and try again.',
      'Each plan must have id, name, and amount': 'All required fields must be filled.',
      'Plan amount must be a non-negative number': 'Plan amount must be $0 or greater.',
      'Plan frequency must be "monthly" or "yearly"': 'Frequency must be Monthly or Yearly.',
      'Plan IDs must be unique': 'Each plan must have a unique ID.',
      'Theme not found': 'Theme not found. Please refresh and try again.',
    };
    return messages[error] || 'An error occurred. Please try again.';
  };

  const headerLabel = (
    <span className="text-muted">
      Manage custom subscription plans for theme
    </span>
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-3 mb-0">Loading custom plans...</p>
          </div>
        </Container>
      </div>
    );
  }

  // Empty state
  if (!hasCustomPlans && plans.length === 0) {
    return (
      <div className="page-content">
        <HeadersRole
          role={"Custom Subscription Plans"}
          icon={adminIcon}
          svg={true}
          text={headerLabel}
        />

        <Container fluid>
          <Button
            color="light"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <i className="ri-arrow-left-line me-1"></i>
            Back
          </Button>

          <Card>
            <CardBody className="text-center py-5">
              <div className="avatar-lg mx-auto mb-4">
                <div className="avatar-title bg-soft-primary text-primary rounded-circle fs-2">
                  <i className="ri-file-list-3-line"></i>
                </div>
              </div>
              <h5 className="mb-3">No Custom Plans</h5>
              <p className="text-muted mb-4">
                This theme is using default plans.<br />
                Create custom plans to override the default pricing for this whitelabel.
              </p>
              <Button color="primary" onClick={addPlan}>
                <i className="ri-add-line me-1"></i>
                Create First Custom Plan
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <HeadersRole
        role={"Custom Subscription Plans"}
        icon={adminIcon}
        svg={true}
        text={headerLabel}
      />

      <Container fluid>
        <Button
          color="light"
          onClick={handleCancel}
          className="mb-4"
        >
          <i className="ri-arrow-left-line me-1"></i>
          Back
        </Button>

        {/* Status Card */}
        <Card className="mb-4">
          <CardBody>
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <h6 className="mb-1">Theme: {themeName}</h6>
                    <small className="text-muted">National ID: {nationalId}</small>
                  </div>
                  {hasCustomPlans && (
                    <Badge color="success" className="fs-6">
                      Custom Plans Active
                    </Badge>
                  )}
                </div>
              </Col>
              {hasCustomPlans && (
                <Col xs="auto">
                  <Button
                    color="danger"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Removing...
                      </>
                    ) : (
                      <>
                        <i className="ri-delete-bin-line me-1"></i>
                        Reset to Default Plans
                      </>
                    )}
                  </Button>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Plans */}
        {plans.map((plan, index) => (
          <Card key={index} className="mb-3">
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Plan {index + 1}</h5>
                <Button
                  color="danger"
                  size="sm"
                  outline
                  onClick={() => removePlan(index)}
                >
                  <i className="ri-close-line me-1"></i>
                  Remove Plan
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for={`plan-id-${index}`}>
                        Plan ID <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id={`plan-id-${index}`}
                        type="text"
                        value={plan.id}
                        onChange={(e) => updatePlan(index, 'id', e.target.value)}
                        placeholder="plan-id"
                        innerRef={index === plans.length - 1 ? firstPlanIdRef : null}
                      />
                      <small className="text-muted">Letters, numbers, and dashes only</small>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for={`plan-name-${index}`}>
                        Plan Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id={`plan-name-${index}`}
                        type="text"
                        value={plan.name}
                        onChange={(e) => updatePlan(index, 'name', e.target.value)}
                        placeholder="Plan Name"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for={`plan-amount-${index}`}>
                        Amount <span className="text-danger">*</span>
                      </Label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <Input
                          id={`plan-amount-${index}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={plan.amount}
                          onChange={(e) => updatePlan(index, 'amount', parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for={`plan-frequency-${index}`}>
                        Frequency <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id={`plan-frequency-${index}`}
                        type="select"
                        value={plan.frequency}
                        onChange={(e) => updatePlan(index, 'frequency', e.target.value)}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for={`plan-description-${index}`}>Description</Label>
                      <Input
                        id={`plan-description-${index}`}
                        type="textarea"
                        rows="3"
                        value={plan.description || ''}
                        onChange={(e) => updatePlan(index, 'description', e.target.value)}
                        placeholder="Optional description..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup check>
                      <Input
                        id={`plan-family-${index}`}
                        type="checkbox"
                        checked={plan.isFamilyPlan || false}
                        onChange={(e) => updatePlan(index, 'isFamilyPlan', e.target.checked)}
                      />
                      <Label check for={`plan-family-${index}`}>
                        Family Plan
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check>
                      <Input
                        id={`plan-free-${index}`}
                        type="checkbox"
                        checked={plan.isFreePlan || false}
                        onChange={(e) => updatePlan(index, 'isFreePlan', e.target.checked)}
                      />
                      <Label check for={`plan-free-${index}`}>
                        Free Plan
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        ))}

        {/* Add Plan Button */}
        <Button
          color="soft-primary"
          className="mb-4"
          onClick={addPlan}
        >
          <i className="ri-add-line me-1"></i>
          Add Plan
        </Button>

        {/* Actions */}
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center">
              <Button
                color="light"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={saving || !isDirty}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>
                    Save Custom Plans
                  </>
                )}
              </Button>
            </div>
            {isDirty && (
              <Alert color="warning" className="mt-3 mb-0">
                <i className="ri-alert-line me-1"></i>
                You have unsaved changes
              </Alert>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ThemeCustomPlans;
